<template>
  <div class="integration-registration-drawer">
    <PDrawer
      :title="drawerTitle"
      small-title
      width="600px"
      v-model="visibleDrawer"
      @close="handleClose"
      @onClose="handleClose"
      @beforeClose="handleClose"
      :close-on-click-modal="false"
    >
      <SecretRevealPanel
        v-if="secretDialog"
        :config="secretDialog.config"
        :value="secretDialog.value"
        @copied="handleSecretCopied"
      />

      <div v-else>
        <el-row>
          <el-col :span="24" style="margin: 0.5rem 0">
            <PDivider label="Integração" style="margin-bottom: 0.5rem" />
          </el-col>
          <el-col :span="24">
            <PSelect
              v-model="apiHash"
              label="Selecione uma integração"
              placeholder="Selecione uma opção"
              :options="listaWebservicesOpt"
              :disabled="loading || isEditMode"
              @change="handleChange"
              @clear="handleClear"
              style="width: 100%"
              clearable
              filterable
            />
          </el-col>
          <el-col
            v-if="erro || blockedMessage"
            :span="24"
            style="margin-top: 0.5rem"
          >
            <PBannerBase type="error">
              <template #title>{{ erro || blockedMessage }}</template>
            </PBannerBase>
          </el-col>
          <el-col
            v-if="showIntegrationManual"
            :span="24"
            style="margin: 0.5rem 0 0"
          >
            <PBannerBase type="info">
              <template #title>
                <div
                  style="
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  "
                >
                  <div style="font-size: 13px; font-weight: 300; width: 90%">
                    Para mais instruções de como cadastrar a integração, acesse
                    o manual completo
                  </div>
                  <PButton type="link" suffix-icon="download">
                    Baixar manual
                  </PButton>
                </div>
              </template>
            </PBannerBase>
          </el-col>
          <el-col :span="24">
            <DynamicIntegracaoForm
              ref="formRef"
              :campos="campos"
              :grupos="grupos"
              :schema="schema"
              :initial-values="initialValues"
              :gateway="gateway"
              :action-context="{ WebserviceId: props.webserviceId }"
              :request-reload-form="handleActionReloadForm"
              @close-drawer="handleActionCloseDrawer"
            />
          </el-col>
        </el-row>
      </div>

      <template #footer>
        <PButton v-if="secretDialog" type="primary" @click="handleClose"
          >Fechar</PButton
        >
        <PButton
          v-else-if="hasSubmit"
          type="primary"
          @click="handleSubmit"
          :loading="loading || isSaving"
          :disabled="isSubmitDisabled"
          >Salvar</PButton
        >
      </template>
    </PDrawer>
  </div>
</template>

<script setup lang="ts">
import {
  PBannerBase,
  PButton,
  PDivider,
  PDrawer,
  PSelect
} from "@f360-dev/f360-prisma";
import { ElMessageBox } from "element-plus";
import { computed, ref, watch } from "vue";
import { EIntegracoesAPIEnum } from "../../../../../@types/IntegracoesAPIEnum";
import useCadastroIntegracao from "../composables/useCadastroIntegracao";
import useBuildLegacyPayload from "../composables/dynamicForm/handlers/useBuildLegacyPayload/useBuildLegacyPayload";
import { DITypes, IDefaultHttpFinancasResponse } from "@/core/@types/types";
import { diContainer } from "@/inversify.config";
import { IToastNotification } from "@/core/composables/notification/types";
import { useIntegracoesStore } from "../../../../../store/useIntegracoesStore";
import { storeToRefs } from "pinia";
import DynamicIntegracaoForm from "../renderers/DynamicIntegracaoForm/DynamicIntegracaoForm.vue";
import SecretRevealPanel from "./SecretRevealPanel/SecretRevealPanel.vue";
import {
  CadastroDeIntegracaoGateway,
  TIntegracaoActionOutcome,
  TIntegracaoActionSecretDialog
} from "../services/CadastroDeIntegracaoGateway";
import useEditActionExecutor from "../composables/editAction/useEditActionExecutor";
import useAnalyticsIntegracoes, {
  AnalyticsIntegracoesResult
} from "../../../../../analytics/useAnalyticsIntegracoes";

const props = withDefaults(
  defineProps<{
    visibleDrawer: boolean;
    gateway: CadastroDeIntegracaoGateway;
    mode?: "create" | "edit";
    webserviceId?: string | null;
    webserviceName?: string | null;
  }>(),
  { mode: "create", webserviceId: null, webserviceName: null }
);

const emit = defineEmits<{
  (e: "onClose", payload: { saved: boolean }): void;
}>();

const notification = diContainer.get<IToastNotification<string>>(
  DITypes.NOTIFICATION
);

const integracoesStore = useIntegracoesStore();
const { listaWebservicesOpt } = storeToRefs(integracoesStore);
const { sendSaveIntegration, sendEditIntegration } = useAnalyticsIntegracoes();

const apiHash = ref<EIntegracoesAPIEnum | undefined>(undefined);
const formRef = ref<InstanceType<typeof DynamicIntegracaoForm>>();
// Ocultação temporária solicitada pela FINC-2134.
const showIntegrationManual = false;

const visibleDrawer = ref(props.visibleDrawer || false);
const isEditMode = computed(() => props.mode === "edit");
const drawerTitle = computed(() =>
  isEditMode.value ? "Editar integração" : "Cadastro da integração"
);

const {
  isLoading: loading,
  erro,
  campos,
  grupos,
  schema,
  initialValues,
  submitAction,
  editAction,
  apiHashEdicao,
  ativoEdicao,
  obterCampos,
  carregarEdicao,
  reset
} = useCadastroIntegracao(props.gateway);

const { buildLegacyPayload } = useBuildLegacyPayload();
const { execute: executeAction, resolveAction } = useEditActionExecutor(
  props.gateway
);
const isSaving = ref(false);
const hasSaved = ref(false);
const secretDialog = ref<{
  config: TIntegracaoActionSecretDialog;
  value: string;
} | null>(null);

const isSubmitDisabled = computed(
  () => !apiHash.value || loading.value || isSaving.value
);

const activeAction = computed(() =>
  isEditMode.value ? editAction.value : submitAction.value
);

const blockedMessage = computed(() =>
  activeAction.value?.type === "blocked"
    ? (activeAction.value.message ?? "Esta integração não pode ser editada.")
    : null
);

const hasSubmit = computed(
  () => !["none", "blocked"].includes(activeAction.value?.type ?? "")
);

const selectedIntegrationName = computed(() => {
  const option = listaWebservicesOpt.value.find(
    (item) => String(item.value) === String(apiHash.value)
  );

  return option?.label || props.webserviceName || String(apiHash.value ?? "");
});

const prepareDrawer = async () => {
  hasSaved.value = false;
  secretDialog.value = null;

  if (!isEditMode.value) {
    apiHash.value = undefined;

    return;
  }

  formRef.value?.resetForm();
  reset();

  const id = props.webserviceId;
  if (!id) return;

  await carregarEdicao(id);
  apiHash.value = apiHashEdicao.value ?? undefined;
};

watch(
  () => [props.visibleDrawer, props.mode, props.webserviceId] as const,
  async ([aberto]) => {
    visibleDrawer.value = aberto;
    if (aberto) await prepareDrawer();
  },
  { immediate: true }
);

const fieldsWithRefetch = computed(() =>
  campos.value.filter((campo) => campo.refetchDefinitionParameter)
);

const getFormValues = (): Record<string, unknown> =>
  ({ ...(formRef.value?.values ?? {}) }) as Record<string, unknown>;

watch(
  () =>
    fieldsWithRefetch.value.map((campo) => getFormValues()[campo.nomeCampo]),
  async (newValues, oldValues) => {
    if (!apiHash.value || !fieldsWithRefetch.value.length) return;
    if (oldValues && newValues.every((value, i) => value === oldValues[i]))
      return;

    const queryParams: Record<string, string> = {};
    fieldsWithRefetch.value.forEach((campo, i) => {
      const value = newValues[i];
      if (campo.refetchDefinitionParameter && value != null && value !== "")
        queryParams[campo.refetchDefinitionParameter] = String(value);
    });

    const hasNoParameters = Object.keys(queryParams).length === 0;
    const hadNoPreviousValues =
      !oldValues || oldValues.every((value) => value == null || value === "");
    if (hasNoParameters && hadNoPreviousValues) return;

    await obterCampos(apiHash.value, {
      queryParams,
      preserveValues: getFormValues()
    });
  }
);

const handleChange = (value: EIntegracoesAPIEnum) => {
  reset();
  formRef.value?.resetForm();
  apiHash.value = value;

  obterCampos(value);
};

const handleClear = () => {
  apiHash.value = undefined;
  reset();
  formRef.value?.resetForm();
};

const handleClose = () => {
  const saved = hasSaved.value;
  secretDialog.value = null;
  visibleDrawer.value = false;
  emit("onClose", { saved });
  hasSaved.value = false;
  handleClear();
};

const handleActionCloseDrawer = () => {
  hasSaved.value = true;
  handleClear();
  handleClose();
};

const handleActionReloadForm = async () => {
  if (!props.webserviceId) return;

  await carregarEdicao(props.webserviceId);
};

const handleSecretCopied = () => {
  notification.showNotification("Chave copiada.", {
    type: "success",
    timeout: 3000
  });
};

const resolveResponseField = (
  response: unknown,
  fieldPath: string
): string | null => {
  const value = fieldPath
    .split(".")
    .reduce<unknown>(
      (acc, key) => (acc as Record<string, unknown> | undefined)?.[key],
      response
    );

  return typeof value === "string" && value ? value : null;
};

const sendSubmitAnalytics = (result: AnalyticsIntegracoesResult) => {
  if (isEditMode.value) {
    sendEditIntegration(selectedIntegrationName.value, result);

    return;
  }

  sendSaveIntegration(selectedIntegrationName.value, result);
};

const runSave = async (
  request: () => Promise<IDefaultHttpFinancasResponse<unknown> | undefined>,
  successMessage: string,
  successOutcome?: TIntegracaoActionOutcome | null
) => {
  isSaving.value = true;
  let savedSuccessfully = false;

  try {
    const response = await request();

    if (response?.Ok) {
      savedSuccessfully = true;
      sendSubmitAnalytics(AnalyticsIntegracoesResult.Success);
      hasSaved.value = true;

      const dialogConfig = successOutcome?.secretDialog;
      const secretValue = dialogConfig
        ? resolveResponseField(response, dialogConfig.valueField)
        : null;

      if (dialogConfig && secretValue) {
        secretDialog.value = { config: dialogConfig, value: secretValue };

        return;
      }

      notification.showNotification(successMessage, {
        type: "success",
        timeout: 3000
      });
      handleClear();
      handleClose();

      return;
    }

    sendSubmitAnalytics(AnalyticsIntegracoesResult.Error);

    void ElMessageBox.alert(
      response?.Error ?? "Não foi possível salvar a integração.",
      "",
      { type: "error", confirmButtonText: "OK" }
    );
  } catch (error) {
    if (!savedSuccessfully) {
      sendSubmitAnalytics(AnalyticsIntegracoesResult.Error);
    }

    void ElMessageBox.alert((error as Error).message, "", {
      type: "error",
      confirmButtonText: "OK"
    });
  } finally {
    isSaving.value = false;
  }
};

const executeSubmitAction = async (values: Record<string, unknown>) => {
  const action = resolveAction(activeAction.value, values);
  const endpoint = action?.endpoint;
  if (!action || action.type === "blocked" || !endpoint) return;

  if (action.confirm?.message) {
    try {
      await ElMessageBox.confirm(
        action.confirm.message,
        action.confirm.title ?? undefined,
        {
          confirmButtonText: action.confirm.confirmButtonText ?? "Confirmar",
          cancelButtonText: action.confirm.cancelButtonText ?? "Cancelar"
        }
      );
    } catch {
      return;
    }
  }

  const request = () =>
    executeAction({
      action,
      values,
      apiHash: apiHash.value!,
      webserviceId: isEditMode.value ? props.webserviceId : null,
      fields: campos.value,
      inativo:
        isEditMode.value && ativoEdicao.value !== null
          ? !ativoEdicao.value
          : null
    });

  await runSave(
    request,
    action.result?.success?.message ??
      (isEditMode.value
        ? "Integração alterada com sucesso"
        : "Integração salva com sucesso"),
    action.result?.success
  );
};

const executeLegacySave = async (values: Record<string, unknown>) => {
  const api = apiHash.value;
  if (!api) return;

  const successOutcome = activeAction.value?.result?.success;
  const payload = buildLegacyPayload({
    apiHash: api,
    fields: campos.value,
    values,
    webserviceId: isEditMode.value ? props.webserviceId : null,
    inativo:
      isEditMode.value && ativoEdicao.value !== null ? !ativoEdicao.value : null
  });

  await runSave(
    () =>
      isEditMode.value
        ? props.gateway.editWebservice(payload)
        : props.gateway.saveWebservice(payload),
    successOutcome?.message ??
      (isEditMode.value
        ? "Integração alterada com sucesso"
        : "Integração salva com sucesso"),
    successOutcome
  );
};

const handleSubmit = async () => {
  if (!hasSubmit.value) return;

  const run = formRef.value?.handleSubmit(
    async (values) => {
      const formValues = { ...values } as Record<string, unknown>;

      if (
        activeAction.value?.endpoint ||
        activeAction.value?.type === "conditional"
      ) {
        await executeSubmitAction(formValues);

        return;
      }

      await executeLegacySave(formValues);
    },
    () => {}
  );
  await run?.();
};
</script>

<style scoped></style>
